import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import AuthService from '../services/auth';
import apiService from '../services/api';

// Auth Context
const AuthContext = createContext();

// Auth actions
const AUTH_ACTIONS = {
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  LOGOUT: 'LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null
};

// Auth Context Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing authentication...');
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
        
        const token = AuthService.getToken();
        console.log('🔍 Stored token exists:', !!token);
        
        if (token) {
          const user = AuthService.getUser();
          console.log('🔍 Stored user exists:', !!user);
          
          if (user) {
            // We have both token and user, validate token
            const isValid = await AuthService.validateToken();
            if (isValid) {
              console.log('✅ Auth initialization successful');
              // validateToken() refetched the profile — dispatch the fresh copy,
              // not the pre-validation snapshot, so server-side changes (e.g.
              // otp_verified after call verification) appear without re-login.
              dispatch({ type: AUTH_ACTIONS.SET_USER, payload: AuthService.getUser() || user });
              return;
            }
          }
          
          // Token or user invalid, clear auth
          console.log('❌ Invalid auth data, clearing');
          AuthService.logout();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        } else {
          console.log('ℹ️ No stored authentication found');
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        AuthService.logout();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } finally {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    // Delay initialization slightly to ensure DOM is ready
    const timer = setTimeout(initializeAuth, 100);
    
    // Listen for auth token invalidation events
    const handleTokenInvalid = () => {
      console.log('🔓 Token invalidated, logging out');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };

    window.addEventListener('authTokenInvalid', handleTokenInvalid);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('authTokenInvalid', handleTokenInvalid);
    };
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await AuthService.login(credentials);
      
      if (result && result.success) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: result.user });
        return { success: true, user: result.user };
      } else {
        const errorMsg = result?.error || 'Login failed';
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error('AuthContext login error:', error);
      const errorMessage = error.message || 'Login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await AuthService.register(userData);

      if (result && result.success) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: result.user });
        return { success: true, user: result.user };
      } else {
        const errorMsg = result?.error || 'Registration failed';
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error('AuthContext register error:', error);
      const errorMessage = error.message || 'Registration failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Demo login function
  const demoLogin = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await login({
        phone: 'demo123',
        password: 'demo123'
      });

      return result;
    } catch (error) {
      console.error('AuthContext demo login error:', error);
      const errorMessage = error.message || 'Demo login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };


  // Logout function
  const logout = () => {
    AuthService.logout();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Refresh user data function (memoized to prevent unnecessary re-renders)
  const refreshUser = useCallback(async () => {
    try {
      // Use AuthService to get token (uses correct environment variable key)
      const token = AuthService.getToken();
      if (!token) {
        console.log('⚠️ refreshUser: No token found');
        return;
      }

      console.log('🔄 refreshUser: Fetching latest user data...');
      const response = await apiService.getProfile();
      if (response && response.user) {
        console.log('✅ refreshUser: User data updated');
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.user });
        // Update localStorage with fresh user data (use correct key)
        const userKey = import.meta.env.VITE_USER_DATA_KEY || 'user';
        localStorage.setItem(userKey, JSON.stringify(response.user));
      }
    } catch (error) {
      console.error('❌ refreshUser error:', error);
    }
  }, []); // dispatch is stable from useReducer, no dependencies needed

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const value = {
    ...state,
    login,
    register,
    demoLogin,
    logout,
    clearError,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;